import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';
import { format, formatDistanceToNow } from 'date-fns';
import {
    Bell,
    Shield,
    Camera,
    Copy,
    Monitor,
    Eye,
    AlertTriangle,
    ChevronDown,
    Loader2
} from 'lucide-react';

// Map violation types to user-friendly labels and icons
const VIOLATION_INFO = {
    'screenshot_printscreen': { label: 'Screenshot (PrintScreen)', icon: Camera, color: 'text-red-500' },
    'screenshot_snipping_tool': { label: 'Snipping Tool', icon: Camera, color: 'text-red-500' },
    'screenshot_mac': { label: 'Mac Screenshot', icon: Camera, color: 'text-red-500' },
    'screenshot_attempt': { label: 'Screenshot Attempt', icon: Camera, color: 'text-red-500' },
    'copy_attempt': { label: 'Copy Attempt', icon: Copy, color: 'text-amber-500' },
    'copy_keyboard': { label: 'Keyboard Copy', icon: Copy, color: 'text-amber-500' },
    'cut_attempt': { label: 'Cut Attempt', icon: Copy, color: 'text-amber-500' },
    'print_attempt': { label: 'Print Attempt', icon: Monitor, color: 'text-amber-500' },
    'devtools_attempt': { label: 'DevTools', icon: Eye, color: 'text-slate-500' },
    'devtools_f12': { label: 'F12 Key', icon: Eye, color: 'text-slate-500' },
    'tab_hidden': { label: 'Tab Switch', icon: Monitor, color: 'text-slate-400' },
    'window_blur': { label: 'Window Blur', icon: Monitor, color: 'text-slate-400' },
    'focus_tab_switch': { label: 'Left the page (tab)', icon: Monitor, color: 'text-amber-500' },
    'focus_window_blur': { label: 'Left the page (window)', icon: Monitor, color: 'text-amber-500' },
    'focus_mouse_leave': { label: 'Left the page (mouse)', icon: Monitor, color: 'text-amber-500' },
    'focus_signed_out': { label: 'Signed out for leaving', icon: Eye, color: 'text-red-500' },
    'paste_blocked': { label: 'Paste blocked', icon: Copy, color: 'text-amber-500' },
};

const ViolationBell = () => {
    const { user, api } = useAuth();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);

    // Check if admin - we'll use this to conditionally render
    const isAdmin = user?.role === 'admin';

    const fetchViolations = useCallback(async (skip = 0, append = false) => {
        if (isAdmin) return; // Don't fetch for admins
        
        if (skip === 0) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        
        try {
            const res = await api.get(`/content-protection/my-violations?days=30&limit=20&skip=${skip}`);
            
            if (append && data) {
                setData({
                    ...res.data,
                    violations: [...data.violations, ...res.data.violations]
                });
            } else {
                setData(res.data);
            }
        } catch (err) {
            console.error('Failed to fetch violations:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [api, data, isAdmin]);

    // Fetch on open
    useEffect(() => {
        if (open && !data && !isAdmin) {
            fetchViolations();
        }
    }, [open, data, isAdmin, fetchViolations]);

    // Refresh periodically when open
    useEffect(() => {
        if (!open || isAdmin) return;
        
        const interval = setInterval(() => {
            fetchViolations(0, false);
        }, 30000); // Every 30 seconds
        
        return () => clearInterval(interval);
    }, [open, isAdmin, fetchViolations]);

    const loadMore = () => {
        if (data && data.has_more) {
            fetchViolations(data.violations.length, true);
        }
    };

    // Don't render for admins - they have their own panel
    if (isAdmin) {
        return null;
    }

    const totalCount = data?.total_count || 0;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    data-testid="violation-bell"
                >
                    <Bell className="h-5 w-5" />
                    {totalCount > 0 && (
                        <Badge 
                            className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-[10px] bg-red-500 text-white border-0"
                        >
                            {totalCount > 99 ? '99+' : totalCount}
                        </Badge>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent 
                className="w-80 p-0" 
                align="end"
                data-testid="violation-bell-popover"
            >
                {/* Header */}
                <div className="p-3 border-b bg-gradient-to-r from-red-50 to-amber-50">
                    <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-red-500" />
                        <span className="font-semibold text-sm">Activity Monitoring</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                        Your activity is being monitored for security purposes
                    </p>
                </div>

                {/* Stats Summary */}
                {data && totalCount > 0 && (
                    <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-muted-foreground">Last 30 days</p>
                            <p className="text-lg font-bold text-red-600">{totalCount} events</p>
                        </div>
                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                            {Object.entries(data.type_breakdown || {}).slice(0, 3).map(([type, count]) => {
                                const info = VIOLATION_INFO[type] || { label: type, color: 'text-slate-500' };
                                return (
                                    <Badge 
                                        key={type} 
                                        variant="outline" 
                                        className="text-[10px] px-1.5 py-0"
                                    >
                                        {count}
                                    </Badge>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Violations List */}
                <ScrollArea className="h-[300px]">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : totalCount === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                            <Shield className="h-10 w-10 text-green-500 mb-2" />
                            <p className="font-medium text-green-700">All Clear!</p>
                            <p className="text-xs text-muted-foreground">
                                No activity flags in the last 30 days
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {data?.violations?.map((v, idx) => {
                                const info = VIOLATION_INFO[v.type] || { 
                                    label: v.type, 
                                    icon: AlertTriangle, 
                                    color: 'text-slate-500' 
                                };
                                const Icon = info.icon;
                                
                                return (
                                    <div 
                                        key={idx} 
                                        className="p-3 hover:bg-slate-50 transition-colors"
                                    >
                                        <div className="flex items-start gap-2">
                                            <Icon className={`h-4 w-4 mt-0.5 ${info.color}`} />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium">
                                                    {info.label}
                                                </p>
                                                {v.page && (
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {v.page}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground mt-0.5">
                                                    {v.recorded_at 
                                                        ? formatDistanceToNow(new Date(v.recorded_at), { addSuffix: true })
                                                        : '-'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            
                            {/* Load More */}
                            {data?.has_more && (
                                <div className="p-3">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="w-full"
                                        onClick={loadMore}
                                        disabled={loadingMore}
                                    >
                                        {loadingMore ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4 mr-1" />
                                        )}
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </ScrollArea>

                {/* Footer Warning */}
                <div className="p-2 border-t bg-amber-50 text-center">
                    <p className="text-[10px] text-amber-700">
                        All activity is logged and may be reviewed by administrators
                    </p>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default ViolationBell;
